import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';

import { useInstallationGuide } from '../../../hooks/useInstallationGuide';
import { GuidanceSection } from '../GuidanceSection';
import { WizardNavigation } from '../WizardNavigation';

/** Path to the Core power light image */
const CORE_POWER_LIGHT_PATH = '/assets/images/installation-guide/core-power-light.png';

/**
 * ConfirmPowerStep - Slide 2.9: Confirm Power On
 *
 * Shows instructions for verifying the Core is powered on:
 * - Image of Core front panel with green power LED
 * - Instructions to look for green light
 * - Two chatbot help buttons for power troubleshooting and cable issues
 */
export const ConfirmPowerStep: React.FC = () => {
  const { goToStep, setMainStepId } = useInstallationGuide();

  useEffect(() => {
    setMainStepId('step_connectCoreToPower');
  }, [setMainStepId]);

  const handlePoweredOn = () => {
    goToStep('ready-test');
  };

  const guidanceButtons = [
    { label: __("My Core isn't turning on"), flowId: 'core-power-troubleshooting' },
    { label: __("Power cord won't reach"), flowId: 'power-cable-too-short' },
  ];

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h3" component="h1" sx={{ mb: 2, textAlign: 'center' }}>
        {__("Let's make sure your Core is powered on.")}
      </Typography>

      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
        {__('After plugging in power, your Core should ')}<strong>{__('turn on automatically')}</strong>{__('. Once you see a ')}<strong>{__('green light')}</strong>{__(", you're ready to continue.")}
      </Typography>

      {/* Core power light image */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          my: 2,
        }}
      >
        <Box
          component="img"
          src={CORE_POWER_LIGHT_PATH}
          alt={__('Core front panel with green power light')}
          sx={{
            maxWidth: '100%',
            maxHeight: 280,
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Guidance section with chatbot buttons */}
      <GuidanceSection
        buttons={guidanceButtons}
      />

      <WizardNavigation
        primaryLabel={__('My Core is powered on')}
        onPrimary={handlePoweredOn}
      />
    </Box>
  );
};
